import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, XCircle } from 'lucide-react'
import Logo from '../components/Logo'

// Password strength checker
const checkPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  }
  
  const passedChecks = Object.values(checks).filter(Boolean).length
  const strength = passedChecks < 3 ? 'weak' : passedChecks < 5 ? 'medium' : 'strong'
  
  return { checks, strength, passedChecks }
}

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const navigate = useNavigate()

  const passwordStrength = checkPasswordStrength(password)
  const isPasswordValid = isSignUp ? passwordStrength.strength === 'strong' : true

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isSignUp && !isPasswordValid) {
      setError('Please create a stronger password')
      return
    }
    
    setLoading(true)
    setError('')
    setSuccess('') // Clear previous success messages

    try {
      console.log('🔐 Starting auth process for:', email)
      
      if (isSignUp) {
        // Check if user already exists in our users table
        console.log('Checking for existing user with email:', email)
        
        const { data: existingUser, error: checkError } = await supabase
          .from('users')
          .select('email')
          .eq('email', email)
          .single()

        console.log('Users table check result:', { existingUser, checkError })

        if (existingUser) {
          console.log('User found in users table, showing error')
          setError('An account with this email already exists. Please sign in instead.')
          setLoading(false)
          return
        }

        console.log('User not found in users table, proceeding with signup')

        // If user doesn't exist, proceed with signup
        console.log('📝 Attempting signup for:', email)
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
            },
          },
        })
        
        console.log('📝 Signup result:', { data, error })
        
        if (error) {
          console.error('❌ Signup error:', error)
          // Check for specific error messages that indicate existing account
          if (error.message.includes('already registered') || 
              error.message.includes('already exists') ||
              error.message.includes('User already registered') ||
              error.message.includes('User already signed up')) {
            setError('An account with this email already exists. Please sign in instead.')
          } else {
            throw error
          }
        } else {
          // Check if user was actually created or if it's an existing user
          if (data.user && !data.session) {
            // This means a confirmation email was sent
            setEmail('')
            setPassword('')
            setFullName('')
            setError('')
            // Show success message instead of alert
            setSuccess('Account created successfully! Please check your email for the confirmation link.')
          } else if (data.user && data.session) {
            // This means the user was already confirmed and we can sign them in
            setError('An account with this email already exists. Please sign in instead.')
          } else {
            // Something unexpected happened
            setError('Something went wrong. Please try again.')
          }
        }
      } else {
        console.log('🔑 Attempting signin for:', email)
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        })
        console.log('🔑 Signin result:', { error })
        if (error) {
          console.error('❌ Signin error:', error)
          throw error
        }
        navigate('/dashboard')
      }
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      setError('Please enter your email address')
      return
    }
    
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      })
      
      if (error) {
        throw error
      }
      
      setSuccess('Password reset link sent! Please check your email.')
      setEmail('')
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
      <div className="w-full max-w-md">
        <div className="card">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-4">
              <Logo />
            </Link>
            <h1 className="text-2xl font-bold text-text">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isSignUp 
                ? 'Join us to start converting meeting notes to Trello tasks' 
                : 'Welcome back! Sign in to continue'
              }
            </p>
          </div>

          <form onSubmit={showForgotPassword ? handleForgotPassword : handleAuth} className="space-y-4">
            {isSignUp && !showForgotPassword && (
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="input pl-10"
                    placeholder="Enter your full name"
                    required={isSignUp}
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {!showForgotPassword && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-10"
                    placeholder="Enter your password"
                    required={!showForgotPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Password strength indicators - only show on signup */}
                {isSignUp && password && (
                  <div className="mt-2 space-y-2">
                    <div className="text-xs text-gray-600">
                      Password strength: 
                      <span className={`ml-1 font-medium ${
                        passwordStrength.strength === 'strong' ? 'text-green-600' :
                        passwordStrength.strength === 'medium' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {passwordStrength.strength}
                      </span>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-center text-xs">
                        {passwordStrength.checks.length ? (
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="w-3 h-3 text-gray-300 mr-2" />
                        )}
                        <span className={passwordStrength.checks.length ? 'text-green-600' : 'text-gray-500'}>
                          At least 8 characters
                        </span>
                      </div>
                      
                      <div className="flex items-center text-xs">
                        {passwordStrength.checks.uppercase ? (
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="w-3 h-3 text-gray-300 mr-2" />
                        )}
                        <span className={passwordStrength.checks.uppercase ? 'text-green-600' : 'text-gray-500'}>
                          One uppercase letter
                        </span>
                      </div>
                      
                      <div className="flex items-center text-xs">
                        {passwordStrength.checks.lowercase ? (
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="w-3 h-3 text-gray-300 mr-2" />
                        )}
                        <span className={passwordStrength.checks.lowercase ? 'text-green-600' : 'text-gray-500'}>
                          One lowercase letter
                        </span>
                      </div>
                      
                      <div className="flex items-center text-xs">
                        {passwordStrength.checks.number ? (
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="w-3 h-3 text-gray-300 mr-2" />
                        )}
                        <span className={passwordStrength.checks.number ? 'text-green-600' : 'text-gray-500'}>
                          One number
                        </span>
                      </div>
                      
                      <div className="flex items-center text-xs">
                        {passwordStrength.checks.special ? (
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                        ) : (
                          <XCircle className="w-3 h-3 text-gray-300 mr-2" />
                        )}
                        <span className={passwordStrength.checks.special ? 'text-green-600' : 'text-gray-500'}>
                          One special character
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-600 text-sm bg-green-50 p-3 rounded-md">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="font-medium">
                    {showForgotPassword ? 'Reset Link Sent!' : 'Account Created Successfully!'}
                  </span>
                </div>
                <p className="mb-3">{success}</p>
                {!showForgotPassword && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignUp(false)
                      setSuccess('')
                      setError('')
                    }}
                    className="text-green-700 hover:text-green-800 font-medium text-sm underline"
                  >
                    Sign in to your account
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {loading ? (
                <div className="loading-spinner mx-auto"></div>
              ) : (
                showForgotPassword ? 'Send Reset Link' : (isSignUp ? 'Create Account' : 'Sign In')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            {showForgotPassword ? (
              <p className="text-gray-600">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setError('')
                    setSuccess('')
                  }}
                  className="text-primary-500 hover:text-primary-600 font-medium"
                >
                  Sign In
                </button>
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-gray-600">
                  {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignUp(!isSignUp)
                      setError('')
                      setSuccess('')
                    }}
                    className="text-primary-500 hover:text-primary-600 font-medium"
                  >
                    {isSignUp ? 'Sign In' : 'Sign Up'}
                  </button>
                </p>
                {!isSignUp && (
                  <p className="text-gray-600">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true)
                        setError('')
                        setSuccess('')
                      }}
                      className="text-primary-500 hover:text-primary-600 font-medium"
                    >
                      Forgot your password?
                    </button>
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              By continuing, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 